/**
 * DotsMenu.tsx — a small trigger-anchored dropdown for row/file actions.
 *
 * Renders a `⋯` button; clicking it opens an absolutely-positioned menu next
 * to the trigger. Reuses the same overlay + Escape + outside-click dismissal
 * pattern as ContextMenu, but is triggered by an explicit button rather than
 * a right-click — the affordance Linear/Notion use for list-row actions.
 *
 * Designed to be the common "more actions" affordance across the app (library
 * rows today; future "open from different sources" patterns reuse it).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { DotsThreeVertical } from '@phosphor-icons/react';

export interface DotsMenuItem {
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

interface DotsMenuProps {
  items: DotsMenuItem[];
  /** Accessible label for the trigger button. */
  label?: string;
  /** Render a custom trigger instead of the default ⋯ icon button. */
  trigger?: (open: boolean) => ReactNode;
  /** Extra class on the trigger wrapper. */
  className?: string;
  /** Stop click propagation from the trigger (useful inside clickable rows). */
  stopPropagation?: boolean;
}

export function DotsMenu({
  items,
  label = 'More actions',
  trigger,
  className,
  stopPropagation = true,
}: DotsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; dropUp: boolean }>({
    top: 0,
    left: 0,
    dropUp: false,
  });

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the menu below (or above, near the viewport bottom) the trigger.
  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const menuW = 220;
    const estH = items.length * 34 + 10;
    const spaceBelow = window.innerHeight - r.bottom;
    const dropUp = spaceBelow < estH + 8 && r.top > estH;
    const left = Math.min(r.left, window.innerWidth - menuW - 8);
    setPos({
      top: dropUp ? r.top - estH - 4 : r.bottom + 4,
      left: left < 8 ? 8 : left,
      dropUp,
    });
  }, [open, items.length]);

  return (
    <div className={`dots-menu${className ? ` ${className}` : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`dots-trigger${open ? ' open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {trigger ? trigger(open) : <DotsThreeVertical size={16} weight="bold" />}
      </button>
      {open && (
        <div
          className={`dots-menu-panel${pos.dropUp ? ' drop-up' : ''}`}
          role="menu"
          ref={menuRef}
          style={{ top: 'inherit', left: 'inherit', zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={`dots-menu-item${it.danger ? ' danger' : ''}`}
              disabled={it.disabled}
              onClick={(e) => {
                e.stopPropagation();
                if (it.disabled) return;
                it.onSelect();
                setOpen(false);
              }}
            >
              {it.icon && <span className="dots-menu-icon">{it.icon}</span>}
              <span className="dots-menu-label">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
